import { type AvatarCard_projects$key } from '#app/__generated__/relay/AvatarCard_projects.graphql.js';
import { Card } from '#app/components/Card/src/Card.js';
import { Link } from '#app/components/Link/index.js';
import { ProjectCoverWrapper } from '#app/components/ProjectCover/ProjectCoverWrapper.js';
import { type ContraImage } from '../Avatar/types.js';
import { AvatarWithDetails } from '../AvatarWithDetails/AvatarWithDetails.js';
import { graphql, useFragment } from '@contra/relay';
import { css, styled } from 'styled-components';

const AvatarCardContainer = styled(Card)`
  ${({ theme }) => css`
    margin-left: auto;
    padding: 16px;
    background: ${theme.colorsV2.white};
    box-shadow: 0px 0px 40px rgba(0, 0, 0, 0.1);
    min-width: 240px;
  `}
`;

const ProjectCoverContainer = styled.div<{ hasProjects: boolean }>`
  ${({ hasProjects }) => css`
    display: flex;
    flex-direction: row;

    & > * + * {
      margin-left: 16px;
    }

    ${hasProjects &&
    css`
      margin-top: 16px;
    `}
  `}
`;

const Content = styled.div`
  border-radius: 4px;
  width: 170px;
`;

type AvatarCardProps = {
  readonly avatarImage?: ContraImage | null;
  readonly deactivated?: boolean;
  readonly displayUsername?: string;
  readonly portfolioProjects?: AvatarCard_projects$key;
  readonly userName?: string;
};

export const AvatarCard = ({
  avatarImage,
  deactivated,
  displayUsername,
  portfolioProjects,
  userName,
}: AvatarCardProps) => {
  const data = useFragment<AvatarCard_projects$key>(
    graphql`
      fragment AvatarCard_projects on UserProfilePortfolioProjectConnection {
        edges {
          node {
            id
            slug
            cover {
              ...ProjectCoverWrapper_portfolioProjectCover
            }
          }
        }
      }
    `,
    portfolioProjects ?? null,
  );

  return (
    <AvatarCardContainer cardRadiusSeverity="light">
      <AvatarWithDetails
        avatarImage={avatarImage}
        deactivated={deactivated}
        displayUsername={displayUsername}
        userName={userName}
      />
      <ProjectCoverContainer hasProjects={Boolean(data?.edges.length)}>
        {data?.edges.length
          ? data.edges.map(({ node: { cover, id, slug } }) => (
              <Link
                key={id}
                to={`/p/${slug}`}
              >
                <Content>
                  <ProjectCoverWrapper coverRef={cover} />
                </Content>
              </Link>
            ))
          : null}
      </ProjectCoverContainer>
    </AvatarCardContainer>
  );
};
